import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import useCanonicalTag from '../hooks/useCanonicalTag';
import useMetaTags from '../hooks/useMetaTags';
import '../styles/fonts.css';
import '../styles/championships.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { fetchData } from '../services/api';

function seasonLabel(year, quarter) {
  if (!year) return null;
  return quarter ? `${year} S${quarter}` : `${year}`;
}

function FilterSection({ label, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#6e7681', marginBottom: '6px', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        {children}
      </div>
    </div>
  );
}

function FilterBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? '#3e4a57' : 'none',
        border: `1px solid ${active ? '#6e7681' : '#3e4a57'}`,
        borderRadius: '4px', padding: '4px 10px',
        color: active ? '#fff' : '#9ba3ab', fontSize: '12px',
        cursor: 'pointer', transition: '0.15s',
      }}
    >
      {children}
    </button>
  );
}

function Championships() {
  const { t } = useTranslation();
  usePageTitle('Championships');
  useCanonicalTag();
  useMetaTags({
    title: 'Championships | Celeritas',
    description: 'Browse iRacing league championships on Celeritas. Standings, schedules and results.',
    url: 'https://celeritas.live/championships',
  });
  const [championships, setChampionships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [applied, setApplied] = useState({ year: null, quarter: null, category: null, series: null });

  useEffect(() => {
    setLoading(true);
    fetchData(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/championships/`)
      .then(data => {
        setChampionships(data);
        setError(null);
        const withSeason = data.filter(c => c.season_year);
        if (withSeason.length > 0) {
          const maxYear = Math.max(...withSeason.map(c => c.season_year));
          const inMaxYear = withSeason.filter(c => c.season_year === maxYear);
          const maxQuarter = Math.max(...inMaxYear.map(c => c.season_quarter || 0));
          setSelectedYear(maxYear);
          setSelectedQuarter(maxQuarter || null);
          setApplied({ year: maxYear, quarter: maxQuarter || null, category: null, series: null });
        }
      })
      .catch(err => {
        console.error('Erro ao buscar campeonatos:', err);
        setError(t('championships.error'));
      })
      .finally(() => setLoading(false));
  }, []);

  const years = useMemo(() => {
    const set = new Set(championships.filter(c => c.season_year).map(c => c.season_year));
    return [...set].sort((a, b) => b - a);
  }, [championships]);

  const seriesList = useMemo(() => {
    const map = {};
    for (const c of championships) {
      if (c.series_id) map[c.series_id] = c.series_name;
    }
    return Object.entries(map).map(([id, name]) => ({ id: Number(id), name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [championships]);

  const filtered = useMemo(() => {
    return championships.filter(c => {
      if (applied.year && c.season_year !== applied.year) return false;
      if (applied.quarter && c.season_quarter !== applied.quarter) return false;
      if (applied.category && (c.track_category || '').toLowerCase() !== applied.category.toLowerCase()) return false;
      if (applied.series && c.series_id !== applied.series) return false;
      return true;
    });
  }, [championships, applied]);

  if (loading) {
    return (
      <div className="ChampionshipsPage">
        <Header />
        <main>
          <section className="center-col leagues-archive">
            <div className="box-header"><h3>{t('championships.title')}</h3></div>
            <p style={{ color: 'white', fontSize: '20px', textAlign: 'center' }}>{t('championships.loading')}</p>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ChampionshipsPage">
        <Header />
        <main>
          <section className="center-col leagues-archive">
            <div className="box-header"><h3>{t('championships.title')}</h3></div>
            <p className="error-message" style={{ color: 'red', fontSize: '20px', textAlign: 'center' }}>{error}</p>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="ChampionshipsPage">
      <Header />
      <main>
        <div style={{ maxWidth: '1200px', margin: '20px auto', padding: '0 16px' }}>
          <div className="champ-layout">

            <section className="champ-list-col">
              <div className="box-header" style={{ marginBottom: '12px' }}>
                <h3>{t('championships.title')}</h3>
              </div>

              <div className="leagues-container">
                {filtered.map(champ => (
                  <article className="league-card" key={champ.championship_id}>
                    <div className="league-logo-min">
                      <img
                        src={champ.league_logo || '/logo-topone.png'}
                        alt="Logo"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                      />
                    </div>
                    <div className="league-main-info">
                      <h4>
                        <Link to={`/championships/${champ.championship_id}`} className="card-link">
                          {champ.name}
                        </Link>
                        {champ.league_managed && (
                          <span title={t('nav.verifiedTooltip')} style={{
                            marginLeft: 8, fontSize: '0.68rem', fontWeight: 600,
                            background: '#1a3d2b', color: '#4caf50',
                            border: '1px solid #2d6b3a', borderRadius: 4,
                            padding: '1px 7px', verticalAlign: 'middle', cursor: 'default',
                          }}>✓ {t('nav.verifiedBadge')}</span>
                        )}
                      </h4>
                      <p style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {seasonLabel(champ.season_year, champ.season_quarter) && (
                          <span className="season-badge">{seasonLabel(champ.season_year, champ.season_quarter)}</span>
                        )}
                        <span className={champ.active ? 'status-live' : 'status-finished'}>
                          {champ.active ? t('common.active') : t('common.finished')}
                        </span>
                      </p>
                    </div>
                    <div className="league-meta">
                      <Link to={`/championships/${champ.championship_id}`} className="btn-details">
                        {t('common.viewStandings')}
                      </Link>
                    </div>
                  </article>
                ))}
                {filtered.length === 0 && (
                  <p style={{ color: '#9ba3ab', padding: '20px 0' }}>
                    {t('championships.noResults')}
                  </p>
                )}
              </div>
            </section>

            <aside className="champ-filter-col">
              {years.length > 0 && (
                <FilterSection label={t('common.year')}>
                  <FilterBtn active={selectedYear === null} onClick={() => { setSelectedYear(null); setSelectedQuarter(null); }}>{t('common.all')}</FilterBtn>
                  {years.map(y => (
                    <FilterBtn key={y} active={selectedYear === y} onClick={() => { setSelectedYear(y); setSelectedQuarter(null); }}>{y}</FilterBtn>
                  ))}
                </FilterSection>
              )}

              <FilterSection label={t('common.season')}>
                <FilterBtn active={selectedQuarter === null} onClick={() => setSelectedQuarter(null)}>{t('common.all')}</FilterBtn>
                {[1, 2, 3, 4].map(q => (
                  <FilterBtn key={q} active={selectedQuarter === q} onClick={() => setSelectedQuarter(q)}>S{q}</FilterBtn>
                ))}
              </FilterSection>

              <FilterSection label={t('common.trackType')}>
                <FilterBtn active={selectedCategory === null} onClick={() => setSelectedCategory(null)}>{t('common.all')}</FilterBtn>
                {['Road', 'Oval', 'Dirt'].map(cat => (
                  <FilterBtn key={cat} active={selectedCategory === cat} onClick={() => setSelectedCategory(cat)}>{cat}</FilterBtn>
                ))}
              </FilterSection>

              {seriesList.length > 0 && (
                <FilterSection label={t('championships.series')}>
                  <FilterBtn active={selectedSeries === null} onClick={() => setSelectedSeries(null)}>{t('common.all')}</FilterBtn>
                  {seriesList.map(s => (
                    <FilterBtn key={s.id} active={selectedSeries === s.id} onClick={() => setSelectedSeries(s.id)}>{s.name}</FilterBtn>
                  ))}
                </FilterSection>
              )}

              <button
                onClick={() => setApplied({ year: selectedYear, quarter: selectedQuarter, category: selectedCategory, series: selectedSeries })}
                style={{
                  width: '100%', marginTop: '4px',
                  background: '#ff4747', border: 'none',
                  borderRadius: '4px', padding: '7px',
                  color: '#fff', fontSize: '12px', fontWeight: 'bold',
                  cursor: 'pointer', letterSpacing: '0.5px',
                }}
              >
                {t('common.filter')}
              </button>
            </aside>

          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default Championships;
